import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SectionTitle } from "@/components/GlassBits";
import { formatINR, formatDate } from "@/lib/format";
import { Search, ArrowUpRight } from "lucide-react";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("");
  const [status, setStatus] = useState("");
  const [branches, setBranches] = useState([]);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const p = { params: {} };
      if (q) p.params.q = q;
      if (branch) p.params.branch = branch;
      if (status) p.params.status = status;
      const { data } = await api.get("/admin/customers", p);
      setItems(data);
    } catch (e) { console.error("[Customers] load failed", e); }
  }, [q, branch, status]);

  const loadBranches = useCallback(async () => {
    try { const { data } = await api.get("/admin/branches"); setBranches(data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <SectionTitle eyebrow="Book" title="Customers" />
      <div className="ldc-glass p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, phone…" className="ldc-input w-full pl-9 pr-3 py-2 rounded-md text-sm" data-testid="customers-search" />
        </div>
        <select value={branch} onChange={(e) => setBranch(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="customers-branch-filter">
          <option value="">All branches</option>
          {branches.map(b => (<option key={b.id} value={b.name}>{b.name}</option>))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="customers-status-filter">
          <option value="">All payment statuses</option>
          <option value="paid">With paid transactions</option>
          <option value="pending">With pending only</option>
        </select>
      </div>

      <div className="ldc-glass overflow-hidden">
        <div className="overflow-x-auto ldc-scroll">
          <table className="w-full ldc-table" data-testid="customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Branch</th>
                <th>Transactions</th>
                <th>Total Paid</th>
                <th>Last Payment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (<tr><td colSpan={8} className="text-center py-10 text-white/40">No customers yet.</td></tr>)}
              {items.map((c) => (
                <tr key={c.email} onClick={() => nav(`/dashboard/customers/${encodeURIComponent(c.email)}`)} className="cursor-pointer" data-testid={`customer-row-${c.email}`}>
                  <td>
                    <div className="text-white/90">{c.name || "—"}</div>
                  </td>
                  <td className="text-white/70">{c.email}</td>
                  <td className="text-white/60 font-mono text-xs">{c.phone || "—"}</td>
                  <td className="text-white/70">{c.branch || "—"}</td>
                  <td className="font-medium">{c.total_transactions} <span className="text-[10px] text-white/40">/ {c.total_invoices}</span></td>
                  <td className="font-medium text-[#fbce18]">{formatINR(c.total_paid)}</td>
                  <td className="text-xs text-white/50">{c.last_payment_date ? formatDate(c.last_payment_date) : "—"}</td>
                  <td><ArrowUpRight size={14} className="text-white/40" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
